import { SDKError, InternalError } from './SDKError.js';
import { ErrorCategory } from './ErrorCategories.js';
import { ErrorCode } from './ErrorCodes.js';

// Maps the error families that CANNOT extend SDKError onto the SDK error
// contract, keyed by their (stable, preserved) class name.
//
// This is the single deliberate exception to "every SDK error extends
// SDKError", and it is forced by a standing architectural invariant, not by
// convenience: `src/playback/` never imports from `core/`, `protocol/`, or
// `widgets/` - it is the one layer everything else may depend on and that
// depends on nothing above HTML5 Audio itself. Making AudioError extend
// SDKError would have inverted that dependency for the entire audio playback
// engine. Normalizing at the boundary instead keeps the invariant intact and
// still gives audio failures a code, a category, and a recoverability flag.
//
// Note this is NOT "using error.name as the code" (ADR-026 constraint 1) -
// the name is a lookup key at a normalization boundary, and the resulting
// `code` is a stable value from the taxonomy.
const EXTERNAL_FAMILY = Object.freeze({
  AudioLoadError: {
    code: ErrorCode.SDK_AUDIO_LOAD_FAILED,
    category: ErrorCategory.PLAYBACK,
    recoverable: false,
    subsystem: 'audio-playback-engine',
  },
  AudioPlaybackError: {
    // Recoverable: the overwhelmingly common cause is an autoplay-policy
    // rejection, which succeeds on a subsequent user gesture.
    code: ErrorCode.SDK_AUDIO_PLAYBACK_FAILED,
    category: ErrorCategory.PLAYBACK,
    recoverable: true,
    subsystem: 'audio-playback-engine',
  },
  AudioStateError: {
    code: ErrorCode.SDK_LIFECYCLE_INVALID_STATE,
    category: ErrorCategory.LIFECYCLE,
    recoverable: false,
    subsystem: 'audio-playback-engine',
  },
  AudioError: {
    code: ErrorCode.SDK_PLAYBACK_FAILED,
    category: ErrorCategory.PLAYBACK,
    recoverable: false,
    subsystem: 'audio-playback-engine',
  },
});

// Native/third-party error names that carry meaning worth preserving.
const NATIVE_NAME = Object.freeze({
  AbortError: {
    code: ErrorCode.SDK_NETWORK_ABORTED,
    category: ErrorCategory.NETWORK,
    recoverable: true,
  },
  TimeoutError: {
    code: ErrorCode.SDK_NETWORK_TIMEOUT,
    category: ErrorCategory.NETWORK,
    recoverable: true,
  },
});

// Turns anything thrown into an SDKError, at a subsystem or public boundary
// (Milestone 6.10/ADR-026).
//
// The SDK must not expose arbitrary DOMExceptions, fetch rejections, video.js
// MediaErrors, or raw TypeErrors as its stable error contract. Equally, it
// must not wrap an error that is already normalized - so normalize() is
// idempotent by design and returns the SAME INSTANCE for an SDKError,
// preserving both identity and the original cause chain.
export class ErrorNormalizer {
  static normalize(error, options = {}) {
    // Idempotence: already normalized, nothing to do. Re-wrapping here is
    // what would produce the "cause chain five levels deep, all the same
    // error" problem the milestone warns against.
    if (error instanceof SDKError) {
      return error;
    }

    const {
      code = ErrorCode.SDK_INTERNAL_ERROR,
      category = ErrorCategory.INTERNAL,
      recoverable = false,
      subsystem = null,
      operation = null,
      context = {},
      message,
    } = options;

    const classification = classify(error);
    const resolved = {
      code: classification?.code ?? code,
      category: classification?.category ?? category,
      recoverable: classification?.recoverable ?? recoverable,
      subsystem: classification?.subsystem ?? subsystem,
    };

    const resolvedMessage =
      message ?? (error instanceof Error ? error.message : String(error ?? 'Unknown error'));

    // The original is always preserved as `cause` - never flattened into the
    // message, never discarded. A non-Error throw (a string, an object) is
    // still worth keeping, so it is recorded in context rather than as a
    // cause, since `cause` should hold a real Error.
    const cause = error instanceof Error ? error : null;
    const mergedContext =
      error instanceof Error
        ? { originalName: error.name, ...context }
        : { originalValue: String(error ?? 'Unknown error'), ...context };

    if (resolved.code === ErrorCode.SDK_INTERNAL_ERROR) {
      return new InternalError(resolvedMessage, {
        cause,
        context: mergedContext,
        subsystem: resolved.subsystem,
        operation,
        recoverable: resolved.recoverable,
      });
    }

    return new SDKError(resolvedMessage, {
      code: resolved.code,
      category: resolved.category,
      recoverable: resolved.recoverable,
      cause,
      context: mergedContext,
      subsystem: resolved.subsystem,
      operation,
    });
  }

  // The configuration layer deliberately still throws TypeError, unchanged,
  // for backward compatibility (ADR-026, decision R1 - roughly forty existing
  // assertions depend on `toThrow(TypeError)`). This is where such a TypeError
  // becomes part of the one error system, at the widget boundary, without the
  // configuration layer itself changing at all.
  static normalizeConfiguration(error, options = {}) {
    return ErrorNormalizer.normalize(error, {
      code: ErrorCode.SDK_CONFIG_INVALID,
      category: ErrorCategory.CONFIGURATION,
      recoverable: false,
      subsystem: 'configuration',
      operation: 'configure',
      ...options,
    });
  }

  // NOTE: video.js MediaError conversion deliberately does NOT live here.
  // `core/` must never import from `widgets/`, and the translation belongs
  // with the class that represents the result - see
  // `VideoPlaybackError.fromMediaError()` in widgets/video/playback/. Having
  // two places that both know how to translate a MediaError would be exactly
  // the duplicated normalization this milestone removes.

  // True when the value is already part of the SDK error contract - used by
  // boundaries that want to avoid re-classifying something a deeper layer
  // already described accurately.
  static isSDKError(error) {
    return error instanceof SDKError;
  }
}

function classify(error) {
  if (!(error instanceof Error) && !error?.name) {
    return null;
  }
  return EXTERNAL_FAMILY[error.name] ?? NATIVE_NAME[error.name] ?? null;
}
